import qs from 'qs';
import { Button, ButtonVariant } from '@shipengine/giger';
import { getAsset } from '@utils';
import { useLogout } from '@hooks';
import { useTranslation } from '@packlink/translation-provider';
import { useTenantConfig } from '@packlink/tenant-config-provider';
import { ErrorPageTypes } from './ErrorPageTypes';

export interface EbayErrorPageProps {
    errorType: ErrorPageTypes;
}

export const EbayErrorPage = ({ errorType }: EbayErrorPageProps): JSX.Element => {
    const { t } = useTranslation();
    const doLogout = useLogout();

    const {
        externalPages: { isapi: isApiUrl, supportedBrowsers: supportedBrowsersUrl },
    } = useTenantConfig<ITenantConfig>();

    const renderMaintenanceMessage = (): JSX.Element => <p>{t('error.maintenance.message')}</p>;

    const renderNotFoundMessage = (): JSX.Element => (
        <>
            <ul className="error-content__list">
                <li className="error-content__list-item">{t('error.not-found.message')}</li>
                <li className="error-content__list-item">{t('error.import-conditions.message')}</li>
                <li className="error-content__list-item error-content__list-item--has-sublist">
                    <ul className="error-content__sublist">
                        <li className="error-content__list-item">{t('error.import-conditions.condition-1')}</li>
                        <li className="error-content__list-item">{t('error.import-conditions.condition-2')}</li>
                        <li className="error-content__list-item">{t('error.import-conditions.condition-3')}</li>
                        <li className="error-content__list-item">{t('error.import-conditions.condition-4')}</li>
                    </ul>
                </li>
            </ul>
        </>
    );

    const renderSwitchAccountButton = (): JSX.Element => (
        <Button data-id="switch-account-button" onClick={doLogout} variant={ButtonVariant.OUTLINED}>
            {t('error.link.switch-account')}
        </Button>
    );

    const renderMessage = (): JSX.Element => (
        <>
            <p className="error-content__text">{t('error.common.message')}</p>
            {errorType === ErrorPageTypes.NOT_FOUND && renderNotFoundMessage()}
            <p className="error-content__text">
                {t('error.common.message-ext')}&nbsp;
                <a href={supportedBrowsersUrl}>{t('error.link.supported-browsers')}</a>
            </p>
            <p className="error-content__buttons">
                <Button onClick={reloadCurrentPage}>{t('error.link.try-again')}</Button>
                {errorType === ErrorPageTypes.NOT_FOUND && renderSwitchAccountButton()}
            </p>
        </>
    );

    return (
        <>
            <img
                className="error-content__icon"
                src={getAsset('icons/parcel-sad.svg')}
                width="142px"
                height="175px"
                alt="Error icon"
            />
            <h1 className="error-content__title">
                {errorType === ErrorPageTypes.NOT_FOUND && t('error.not-found.title')}
                {errorType === ErrorPageTypes.INTERNAL_SERVER_ERROR && t('error.server-error.title')}
                {errorType === ErrorPageTypes.MAINTENANCE && t('error.maintenance.title')}
            </h1>
            {errorType === ErrorPageTypes.MAINTENANCE ? renderMaintenanceMessage() : renderMessage()}
            <p className="error-content__text">
                <a href={getGoToEbayLink(isApiUrl)}>{t('error.link.go-back')}</a>
            </p>
        </>
    );
};

const reloadCurrentPage = (): void => {
    window.location.reload();
};

const getGoToEbayLink = (isApiUrl: string): string => {
    const goBackParams = {
        MyEbayBeta: null,
        NewPeriod: 'Last62Days',
        View: 'SoldNext',
        Column: 'Unknown',
        SubmitAction: {
            ChangeFilter: 'x',
        },
        NewFilter: 'WaitShipment',
        CurrentPage: 'MyeBayNextSold',
        FormatFilter: 'AllFormat',
    };

    const goBackOptions = {
        allowDots: true,
        strictNullHandling: true,
    };

    return `${isApiUrl}?${qs.stringify(goBackParams, goBackOptions)}`;
};
